import type {
  IRepaymentPlanData,
  IRepaymentPlanEntryData,
  IRepaymentPlanTotalsData,
} from "./types";

const round2 = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

const money = (value: number): string => round2(value).toFixed(2);

export interface IEarlyRepaymentPlan {
  entries: IRepaymentPlanEntryData[];
  totals: IRepaymentPlanTotalsData;
  isEarly: boolean;
  monthsSaved: number;
  interestSaved: number;
  feesSaved: number;
  grossSaved: number;
}

const sumBy = (
  list: IRepaymentPlanEntryData[],
  pick: (entry: IRepaymentPlanEntryData) => number,
): number => list.reduce((acc, entry) => acc + pick(entry), 0);

/**
 * Truncate the backend plan at repayment-month N, ballooning that row's
 * principal to the full balance; full term is a no-op. The backend plan leads
 * with a month-0 row (setup fee), so repayment months are `entries[1..]` and
 * month 0 is always kept. Savings = interest + fees on the skipped months.
 */
export const deriveEarlyRepaymentPlan = (
  plan: IRepaymentPlanData,
  months: number,
): IEarlyRepaymentPlan => {
  const repaymentMonths = plan.entries.length - 1;
  const n = Math.min(Math.max(Math.trunc(months), 1), repaymentMonths);

  if (n >= repaymentMonths) {
    return {
      entries: plan.entries,
      totals: plan.totals,
      isEarly: false,
      monthsSaved: 0,
      interestSaved: 0,
      feesSaved: 0,
      grossSaved: 0,
    };
  }

  const kept = plan.entries.slice(0, n + 1);
  const last = kept[n] as IRepaymentPlanEntryData;

  const payoffPrincipal =
    Number(last.principal) + Number(last.remainingBalance);
  const interestN = Number(last.interest);
  const feesN = Number(last.administrationFees);

  const balloonRow: IRepaymentPlanEntryData = {
    ...last,
    principal: money(payoffPrincipal),
    payment: money(interestN + payoffPrincipal + feesN),
    remainingBalance: "0.00",
  };

  const entries = [...kept.slice(0, n), balloonRow];

  const totalInterest = sumBy(entries, (e) => Number(e.interest));
  const totalPrincipal = sumBy(entries, (e) => Number(e.principal));
  const totalAdministration = sumBy(entries, (e) =>
    Number(e.administrationFees),
  );
  const setupFee = Number(plan.totals.setupFee);
  const amount = Number(plan.totals.totalPrincipal);

  const totalPayment = sumBy(entries, (e) => Number(e.payment));
  const loanPayment = totalInterest + totalPrincipal;
  const setupFeePaidUpfront = plan.setupFeeFinanced ? 0 : setupFee;
  const totalRepaid = loanPayment + totalAdministration + setupFeePaidUpfront;
  const totalCostOfCredit = totalRepaid - amount;

  const totals: IRepaymentPlanTotalsData = {
    totalPayment: money(totalPayment),
    totalInterest: money(totalInterest),
    totalPrincipal: money(totalPrincipal),
    totalAdministrationFee: money(totalAdministration),
    setupFee: money(setupFee),
    totalRepaid: money(totalRepaid),
    totalCostOfCredit: money(totalCostOfCredit),
  };

  const skipped = plan.entries.slice(n + 1);
  const interestSaved = round2(sumBy(skipped, (e) => Number(e.interest)));
  const feesSaved = round2(sumBy(skipped, (e) => Number(e.administrationFees)));
  const grossSaved = round2(interestSaved + feesSaved);

  return {
    entries,
    totals,
    isEarly: true,
    monthsSaved: repaymentMonths - n,
    interestSaved,
    feesSaved,
    grossSaved,
  };
};
